import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FileText, Briefcase, Share2, Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
import { UpliftLogo, UpliftLogoWhite } from '../components/UpliftLogo';

const Landing = () => {
  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'AI Resume Builder',
      description: 'Create professional resumes with AI-powered suggestions. Stand out from the crowd.',
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: 'Job Tracker',
      description: 'Track all your applications in one place. Never miss a follow-up.',
    },
    {
      icon: <Share2 className="w-6 h-6" />,
      title: 'Content Repurposer',
      description: 'Transform your experience into LinkedIn posts, tweets, and portfolio copy.',
    },
  ];

  const benefits = [
    'AI-powered resume optimization',
    'Track unlimited job applications',
    'Generate social media content',
    'Professional templates',
    'Interview preparation tips',
    'ATS-friendly formats',
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 header-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-2" data-testid="uplift-home-link">
                <UpliftLogo size="sm" showText={false} />
              </Link>
              <Link to="/careerboost" className="flex items-center gap-2" data-testid="logo-link">
                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  CareerBoost
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="login-btn">Log in</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 hero-gradient">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 text-indigo-700 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                AI-Powered Career Tools
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Land Your Dream Job <span className="text-indigo-600">Faster</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-lg">
                Build stunning resumes, track applications, and boost your online presence with AI-powered tools designed for modern job seekers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-200 w-full sm:w-auto" data-testid="hero-cta-btn">
                    Start Building Free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="hero-login-btn">
                    I have an account
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <img
                src="https://images.unsplash.com/photo-1760278041809-b157c39c30d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxzdWNjZXNzZnVsJTIwY3JlYXRpdmUlMjBwcm9mZXNzaW9uYWwlMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzcyNzMzNjI5fDA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Professional working"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 animate-slide-in" style={{ animationDelay: '0.4s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Resume Score</p>
                    <p className="text-emerald-600 font-bold">95/100</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Powerful tools designed to help you stand out in your job search
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="feature-card group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600 mb-6 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Why CareerBoost?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                We combine cutting-edge AI with career expertise to give you the edge you need.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1764588029885-d115de3bda1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjB0ZWNoJTIwb2ZmaWNlJTIwZGVzayUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzcyNzMzNjMxfDA&ixlib=rb-4.1.0&q=85&w=600"
                alt="Modern workspace"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-indigo-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Boost Your Career?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join professionals who are landing their dream jobs with CareerBoost.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50 shadow-xl" data-testid="cta-signup-btn">
              Get Started Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <UpliftLogoWhite size="md" showText={true} className="mb-4" />
              <p className="text-slate-400 text-sm max-w-md">
                Uplift helps you rise in every area that matters. 
                Career tools, business planning, and credit repair - all in one place.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/careerboost" className="text-slate-400 hover:text-white">CareerBoost</Link></li>
                <li><Link to="/bizplanai" className="text-slate-400 hover:text-white">BizPlanAI</Link></li>
                <li><Link to="/creditboost" className="text-slate-400 hover:text-white">CreditBoost</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="text-slate-400 hover:text-white">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-slate-400 text-sm">
                  Founded by <span className="text-white font-medium">Robert Elder III</span>, CEO
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  © 2024-2025 Uplift III / Robert Elder III. All Rights Reserved.
                </p>
              </div>
              <p className="text-slate-600 text-xs text-center md:text-right max-w-md">
                Uplift™, CareerBoost™, BizPlanAI™, and CreditBoost™ are trademarks of Robert Elder III. 
                Unauthorized copying is strictly prohibited.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
